import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetOfficeDoctorAttendingsQuery } from '@/hooks/queries/use-get-office-doctor-attendings';
import useDebounce from '@/hooks/use-debounce';
import { doctorFullnameTitleTrad } from '@/utils/doctor';
import { SelectSearchFormOptions } from './select-search-form';

type SelectOfficeDoctorAttendingSelectedValue = {
  id: string;
  label: string;
  lastname: string;
  firstname: string;
  signatureUrl: string | null;
};

export const useSelectSearchDoctorAttendingListOnOfficeViewModel = ({
  officeId,
}: {
  officeId: string;
}) => {
  const { t } = useTranslation();

  const [doctorAttendingSearch, setDoctorAttendingSearch] =
    useState<string>('');
  const debounceDoctorAttendingSearch = useDebounce(doctorAttendingSearch);

  const { data: doctorAttendingsResponse, isLoading: doctorAttendingsLoading } =
    useGetOfficeDoctorAttendingsQuery({
      page: 1,
      per_page: 10,
      officeId: officeId,
      search: debounceDoctorAttendingSearch,
      officeRequired: true,
    });

  const doctorAttendingOptions = useMemo<
    SelectSearchFormOptions<SelectOfficeDoctorAttendingSelectedValue>
  >(() => {
    if (!doctorAttendingsResponse) {
      return [];
    }

    return doctorAttendingsResponse.data.map((item) => {
      const fullnameTrad = doctorFullnameTitleTrad(item);
      return {
        id: item.id,
        label: t(fullnameTrad.key, fullnameTrad.params),
        value: {
          id: item.id,
          label: t(fullnameTrad.key, fullnameTrad.params),
          firstname: item.firstname,
          lastname: item.lastname,
          signatureUrl: item.signatureUrl,
        } satisfies SelectOfficeDoctorAttendingSelectedValue,
        customClass: item.signatureUrl ? '' : 'italic',
      };
    });
  }, [doctorAttendingsResponse, doctorAttendingsLoading]);

  return {
    options: doctorAttendingOptions,
    search: debounceDoctorAttendingSearch,
    api: {
      setSearch: setDoctorAttendingSearch,
      getValuePlaceholder: (
        value?: SelectOfficeDoctorAttendingSelectedValue,
      ) => {
        if (value) {
          return value.label;
        }

        return undefined;
      },
    },
  };
};
